import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

interface ApiKeyRecord {
  keyId: string;
  keyPrefix: string;
  name: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export default function ApiSubscriptionPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirmation dialog
  const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    loadKeys();
  }, [appId]);

  const loadKeys = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const res = await sentinelNetwork.get(`/api-keys?appId=${appId}`);
      const allKeys: ApiKeyRecord[] = res.data?.keys || [];
      setKeys(allKeys);
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      const res = await sentinelNetwork.post('/api-keys', {
        appId,
        name: keyName.trim(),
      });
      setNewRawKey(res.data?.rawKey || null);
      await loadKeys();
      setSnackbar({
        open: true,
        message: 'API key created successfully',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Create API key failed:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to create API key',
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = (keyId: string) => {
    setRevokeKeyId(keyId);
  };

  const confirmRevoke = async () => {
    if (!revokeKeyId) return;
    setRevoking(true);
    try {
      await sentinelNetwork.delete(`/api-keys/${revokeKeyId}?appId=${appId}`);
      await loadKeys();
      setSnackbar({
        open: true,
        message: 'API key revoked',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: 'Failed to revoke API key',
        severity: 'error',
      });
    } finally {
      setRevoking(false);
      setRevokeKeyId(null);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openCreateDialog = () => {
    setKeyName('');
    setNewRawKey(null);
    setCopied(false);
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setNewRawKey(null);
  };

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <KeyRound size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              API Subscription
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage API keys for programmatic access to this project.
          </Typography>
        </Box>

        {/* Create Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={openCreateDialog}
            sx={{ fontWeight: 600 }}
          >
            Create API Key
          </Button>
        </Box>

        {/* Active Keys */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Active Keys ({activeKeys.length})
            </Typography>
            {activeKeys.length > 0 ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Key Prefix</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Last Used</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeKeys.map((key) => (
                      <TableRow key={key.keyId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {key.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            by {key.createdBy}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {key.keyPrefix}•••
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(key.createdAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(key.lastUsedAt)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Revoke">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRevoke(key.keyId)}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card
                elevation={0}
                sx={{ border: '1px dashed', borderColor: 'divider', p: 3 }}
              >
                <Stack alignItems="center" spacing={1}>
                  <KeyRound size={36} color={theme.palette.text.disabled} />
                  <Typography variant="body2" color="text.secondary">
                    No active API keys. Create one to start submitting tasks programmatically.
                  </Typography>
                </Stack>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Revoked Keys */}
        {revokedKeys.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
                Revoked Keys ({revokedKeys.length})
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', opacity: 0.7 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.action.disabled, 0.05) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Key Prefix</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Revoked At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {revokedKeys.map((key) => (
                      <TableRow key={key.keyId}>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {key.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {key.keyPrefix}•••
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(key.revokedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Create Key Dialog */}
      <Dialog open={createOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {newRawKey ? 'API Key Created' : 'Create API Key'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {!newRawKey ? (
              <>
                <TextField
                  label="Key Name"
                  size="small"
                  fullWidth
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g. CI Pipeline, Jenkins"
                  helperText="A descriptive name to identify this key"
                />
              </>
            ) : (
              <Alert
                severity="success"
                action={
                  <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={() => handleCopy(newRawKey)}>
                      <Copy size={16} />
                    </IconButton>
                  </Tooltip>
                }
              >
                <Typography variant="caption" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  {newRawKey}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Copy this key now — it will not be shown again.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={closeCreateDialog} variant="outlined">
            {newRawKey ? 'Done' : 'Cancel'}
          </Button>
          {!newRawKey && (
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={creating || !keyName.trim()}
              startIcon={
                creating ? <CircularProgress size={18} color="inherit" /> : <KeyRound size={18} />
              }
            >
              {creating ? 'Creating...' : 'Create Key'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={Boolean(revokeKeyId)} onClose={() => !revoking && setRevokeKeyId(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke this API key? Any integrations using this key will stop working immediately. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeKeyId(null)} disabled={revoking}>Cancel</Button>
          <Button onClick={confirmRevoke} color="error" variant="contained" disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {revoking ? 'Revoking…' : 'Revoke Key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
